import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, Lock, Mail, ShieldCheck, Sparkles, Truck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

function extractMessage(error) {
  const code = String(error?.code || '');
  if (code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')) {
    return 'Invalid email or password.';
  }
  if (code.includes('too-many-requests')) {
    return 'Too many attempts. Please wait and try again.';
  }
  if (code.includes('popup-closed-by-user')) {
    return 'Login popup was closed.';
  }
  if (code.includes('popup-blocked')) {
    return 'Popup blocked. Please allow popups and try again.';
  }
  return error?.message || 'Unable to login right now.';
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { loginWithEmail, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleEmailLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      await loginWithEmail(email, password);
      navigate('/profile');
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      await loginWithGoogle();
      navigate('/profile');
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="auth-page">
      <article className="auth-shell">
        <span className="auth-orb auth-orb-one" aria-hidden="true" />
        <span className="auth-orb auth-orb-two" aria-hidden="true" />

        <div className="auth-promo">
          <p className="auth-kicker">Secure Pixel Access</p>
          <h1>Welcome back to your pixel world.</h1>
          <p>Sign in to track orders, save your favorite colors, and checkout faster.</p>

          <ul className="auth-benefit-list">
            <li>
              <ShieldCheck size={16} />
              Firebase protected account security
            </li>
            <li>
              <Sparkles size={16} />
              Faster checkout with saved profile details
            </li>
            <li>
              <Truck size={16} />
              Order updates and delivery tracking in dashboard
            </li>
          </ul>

          <div className="auth-highlight">
            <strong>Prepaid Orders Only</strong>
            <p>Delivery estimate 20-25 days across India.</p>
          </div>
        </div>

        <div className="auth-card">
          <h2>Login</h2>
          <p>Use Google or your email and password.</p>

          {error ? <p className="auth-error">{error}</p> : null}

          <button type="button" className="auth-google-btn" onClick={handleGoogleLogin} disabled={loading}>
            Continue with Google <ArrowRight size={16} />
          </button>

          <div className="auth-divider">
            <span>or continue with email</span>
          </div>

          <form className="auth-form" onSubmit={handleEmailLogin}>
            <label htmlFor="email">Email</label>
            <div className="auth-input-wrap">
              <Mail size={16} />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
              />
            </div>

            <label htmlFor="password">Password</label>
            <div className="auth-input-wrap">
              <Lock size={16} />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="auth-eye-btn"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <button type="submit" className="solid-btn auth-submit-btn" disabled={loading}>
              {loading ? 'Signing in...' : 'Login with Email'}
            </button>
          </form>

          <p className="auth-switch">
            New user? <Link to="/signup">Create account</Link>
          </p>
        </div>
      </article>
    </section>
  );
}
